import { Banner, Button, Dialog } from '@cloudflare/kumo';
import { useState } from 'react';
import { Category } from '../types';
import { CategoryList } from './CategoryList';
import { CategoryForm } from './CategoryForm';
import { Pagination } from './Pagination';
import { SearchField } from './ui/SearchField';
import { useCategories } from '../hooks/useCategories';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CategoryModal({ isOpen, onClose }: CategoryModalProps) {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>();

  const pageSize = 5;

  const { categories, total, loading, error, refresh, deleteCategory } = useCategories({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  const totalPages = Math.ceil(total / pageSize);

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingCategory(undefined);
    refresh();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingCategory(undefined);
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="lg" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Gerenciar Categorias</h3>}</Dialog.Title>

        <div className="flex items-center justify-between mb-6">
          <p className="!text-[var(--text-muted)]">Organize seus contatos em categorias</p>
          <Button variant="primary" onClick={() => { setEditingCategory(undefined); setIsFormOpen(true); }}>
            + Nova Categoria
          </Button>
        </div>

        <div className="mb-4">
          <SearchField
            ariaLabel="Buscar categorias"
            placeholder="Buscar categorias..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {error && (
          <div className="mb-4">
            <Banner variant="error" description={<>
              {error} —{' '}
              <button type="button" onClick={refresh} className="underline hover:no-underline">
                Tentar novamente
              </button>
            </>} />
          </div>
        )}

        <div className="min-h-[200px]">
          <CategoryList
            categories={categories}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteCategory}
          />
        </div>

        {totalPages > 1 && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
            <p className="text-center !text-[var(--text-faint)] text-xs mt-2">
              Mostrando {categories.length} de {total} categorias
            </p>
          </div>
        )}
      
</Dialog>
</Dialog.Root>

      {isFormOpen && (
        <CategoryForm
          category={editingCategory}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </>
  );
}
